/* eslint react-hooks/exhaustive-deps: "warn" */

import { ChangeEvent, KeyboardEvent, MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import cn from 'classnames';

import type { JWTData } from 'libs/http/api/dashboard/dashboard.types';
import { dashboard } from 'libs/http/api/dashboard/dashboard';
import { getCookie, getDomainName, setCookie } from 'libs/node';
import { getNextTwoWeek } from 'libs/date';
import { isNotBusinessEmail } from 'libs/string';

import { useRequest } from 'hooks/useRequest';

import { authConfig } from 'features/agents/helpers/utils';

import styles from './AskRegisInput.module.scss';

import { type AskRegisChip, type AskRegisInputProps } from './AskRegisInput.types';

const DEFAULT_CHIPS: AskRegisChip[] = [
  {
    label: 'Verify a supplier',
    query: 'Verify a new supplier: is it active, who are the directors, and are any filings overdue?',
  },
  {
    label: 'Find companies',
    query: 'Find all active UK companies under SIC 63110 — data processing, hosting and related activities',
  },
  {
    label: 'Pull filed financials',
    query:
      "Pull Ocado Group's latest filed financials for the last 5 years — balance sheet, income statement, profit & loss and cash flow",
  },
  { label: 'Map a group', query: 'Map every subsidiary of Siemens AG, in every jurisdiction' },
  { label: 'Monitor changes', query: 'Alert me when the directors, owners or status of a counterparty change' },
];

const normalizeChip = (chip: AskRegisChip) => (typeof chip === 'string' ? { label: chip, query: chip } : chip);

const rnd = (a: number, b: number) => a + Math.random() * (b - a);

type Step = 'prompt' | 'email' | 'code';
type TypePhase = 'type' | 'hold' | 'erase';

const formatError = (value: string | string[] | undefined): string => {
  if (!value) {
    return '';
  }

  return Array.isArray(value) ? value.join(', ') : value;
};

export const AskRegisInput = ({ chips = DEFAULT_CHIPS, className }: AskRegisInputProps) => {
  const normalizedChips = useMemo(() => chips.map(normalizeChip), [chips]);
  const queries = useMemo(() => normalizedChips.map((chip) => chip.query), [normalizedChips]);

  const [step, setStep] = useState<Step>('prompt');
  const [prompt, setPrompt] = useState('');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [emailError, setEmailError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [focused, setFocused] = useState(false);
  const [reduced, setReduced] = useState(false);
  const [typed, setTyped] = useState('');
  const [typingChip, setTypingChip] = useState(0);
  const redirectTimer = useRef<NodeJS.Timeout>(undefined);
  const typeTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const typeRunning = useRef(false);
  const typeState = useRef<{ qi: number; ci: number; phase: TypePhase; picked: boolean }>({
    qi: 0,
    ci: 0,
    phase: 'type',
    picked: false,
  });

  useEffect(() => () => clearTimeout(redirectTimer.current as NodeJS.Timeout), []);

  useEffect(() => {
    setReduced(window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }, []);

  const stepTyping = useCallback(() => {
    if (!queries.length) {
      return;
    }

    const s = typeState.current;
    const q = queries[s.qi];

    if (s.phase === 'type') {
      s.ci += 1;
      setTyped(q.slice(0, s.ci));
      if (s.ci >= q.length) {
        s.phase = 'hold';
        typeTimer.current = setTimeout(stepTyping, s.picked ? 3200 : 1500);
        s.picked = false;

        return;
      }
      const ch = q[s.ci - 1];
      typeTimer.current = setTimeout(stepTyping, ch === ' ' ? rnd(38, 60) : ch === ',' ? 110 : rnd(14, 32));
    } else if (s.phase === 'hold') {
      s.phase = 'erase';
      stepTyping();
    } else {
      s.ci = Math.max(0, s.ci - 3);
      setTyped(q.slice(0, s.ci));
      if (s.ci === 0) {
        s.phase = 'type';
        s.qi = (s.qi + 1) % queries.length;
        setTypingChip(s.qi);
        typeTimer.current = setTimeout(stepTyping, 240);

        return;
      }
      typeTimer.current = setTimeout(stepTyping, 10);
    }
  }, [queries]);

  const startTyping = useCallback(() => {
    if (typeRunning.current || !queries.length) {
      return;
    }
    typeRunning.current = true;
    setTypingChip(typeState.current.qi);
    stepTyping();
  }, [queries.length, stepTyping]);

  const stopTyping = useCallback(() => {
    typeRunning.current = false;
    clearTimeout(typeTimer.current);
  }, []);

  const shouldType = step === 'prompt' && !focused && !prompt && !reduced;

  useEffect(() => {
    if (shouldType) {
      startTyping();
    } else {
      stopTyping();
    }

    return () => clearTimeout(typeTimer.current);
  }, [shouldType, startTyping, stopTyping]);

  const handlePromptFocus = useCallback(() => {
    setFocused(true);
    if (!prompt && typed && typed === queries[typeState.current.qi]) {
      setPrompt(typed);
    }
  }, [prompt, typed, queries]);

  const handlePromptBlur = useCallback(() => {
    setFocused(false);
  }, []);

  const { request: requestRegister, loading: registerLoading, errors: registerErrors } = useRequest<void>();
  const { request: requestVerify, loading: verifyLoading, errors: verifyErrors } = useRequest<JWTData>();

  const loading = registerLoading || verifyLoading;
  const errors = step === 'code' ? verifyErrors : registerErrors;

  const errorMessage =
    emailError ||
    formatError(errors.detail as string | string[]) ||
    formatError(errors.non_field_errors as string | string[]) ||
    formatError(errors.email as string | string[]) ||
    formatError(errors.code as string | string[]);

  const handleBack = useCallback(() => {
    setStep((current) => (current === 'code' ? 'email' : 'prompt'));
  }, []);

  const redirectToAgent = useCallback((promptValue: string) => {
    window.location.href = `${process.env.NEXT_PUBLIC_PLATFORM}/app-esg/esg/agent?prompt=${encodeURIComponent(promptValue)}`;
  }, []);

  const handlePromptChange = useCallback((event: ChangeEvent<HTMLTextAreaElement>) => {
    setPrompt(event.target.value);
  }, []);

  const handleEmailChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
    setEmailError('');
  }, []);

  const handleCodeChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setCode(event.target.value);
  }, []);

  const handleChipClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      const index = Number(event.currentTarget.dataset.index);

      stopTyping();
      setTypingChip(index);
      setPrompt(event.currentTarget.dataset.chip ?? '');
    },
    [stopTyping],
  );

  const onPromptSubmit = useCallback(() => {
    const value = (prompt || queries[typeState.current.qi] || '').trim();

    if (!value) {
      return;
    }

    stopTyping();

    if (value !== prompt) {
      setPrompt(value);
    }

    if (getCookie(authConfig.ACCESS_KEY)) {
      redirectToAgent(value);

      return;
    }

    setStep('email');
  }, [prompt, queries, stopTyping, redirectToAgent]);

  const handlePromptKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        onPromptSubmit();
      }
    },
    [onPromptSubmit],
  );

  const onEmailSubmit = useCallback(async () => {
    if (!email.trim() || loading) {
      return;
    }

    if (isNotBusinessEmail(email)) {
      setEmailError('Please enter your business email address');

      return;
    }

    setEmailError('');

    try {
      await requestRegister(dashboard.auth.registerRegis({ email }));
      setStep('code');
    } catch (err: any) {
      // 409: user already registered — notify, then redirect to their app with the prompt so the result is shown
      if (err?.code === 'application_already_added' && err?.redirect) {
        const separator = err.redirect.includes('?') ? '&' : '?';
        const target = `${err.redirect}${separator}prompt=${encodeURIComponent(prompt)}`;
        setSuccessMessage('It looks like you already have an account. We will take you now to the login page…');
        redirectTimer.current = setTimeout(() => {
          window.location.href = target;
        }, 3000);
      }
    }
  }, [email, loading, prompt, requestRegister]);

  const onCodeSubmit = useCallback(async () => {
    if (!code.trim() || loading) {
      return;
    }
    try {
      const { access, refresh } = await requestVerify(dashboard.auth.verify({ email, code }));
      if (!access || !refresh) {
        return;
      }

      const cookieOptions = {
        expires: getNextTwoWeek(),
        path: '/',
        domain: getDomainName(window.location.hostname),
      };
      setCookie(authConfig.ACCESS_KEY, access, cookieOptions);
      setCookie(authConfig.REFRESH_KEY, refresh, cookieOptions);

      redirectToAgent(prompt);
    } catch {
      // errors handled via `errors` state from useRequest
    }
  }, [code, email, loading, prompt, redirectToAgent, requestVerify]);

  const handleEmailKeyDown = useCallback(
    (ev: KeyboardEvent<HTMLInputElement>) => {
      if (ev.key === 'Enter') {
        ev.preventDefault();
        onEmailSubmit();
      }
    },
    [onEmailSubmit],
  );

  const handleCodeKeyDown = useCallback(
    (ev: KeyboardEvent<HTMLInputElement>) => {
      if (ev.key === 'Enter') {
        ev.preventDefault();
        onCodeSubmit();
      }
    },
    [onCodeSubmit],
  );

  return (
    <div className={cn(styles.search, className)}>
      <div className={cn(styles.inputBox, step !== 'prompt' && styles.inputBoxStep)}>
        {step !== 'prompt' && (
          <button type="button" className={styles.backBtn} onClick={handleBack} aria-label="Back">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path
                d="M15 18l-6-6 6-6"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}

        {step === 'prompt' && (
          <div className={styles.promptField}>
            <textarea
              className={styles.textarea}
              rows={4}
              placeholder={reduced ? queries[0] : ''}
              value={prompt}
              onChange={handlePromptChange}
              onFocus={handlePromptFocus}
              onBlur={handlePromptBlur}
              onKeyDown={handlePromptKeyDown}
            />

            {shouldType && (
              <div className={styles.typeOverlay} aria-hidden="true">
                <span>{typed}</span>
                <span className={styles.caret} />
              </div>
            )}
          </div>
        )}

        {step === 'email' && (
          <input
            type="email"
            autoFocus
            className={cn(styles.textarea, styles.stepInput)}
            placeholder="Enter your email"
            value={email}
            onChange={handleEmailChange}
            onKeyDown={handleEmailKeyDown}
          />
        )}

        {step === 'code' && (
          <input
            type="text"
            autoFocus
            inputMode="numeric"
            autoComplete="one-time-code"
            className={cn(styles.textarea, styles.stepInput)}
            placeholder="Enter verification code"
            value={code}
            onChange={handleCodeChange}
            onKeyDown={handleCodeKeyDown}
          />
        )}

        <div className={styles.toolbar}>
          {step === 'prompt' && (
            <button
              type="button"
              onClick={onPromptSubmit}
              className={cn(styles.sendBtn, !prompt.trim() && styles.sendBtnInactive)}
              aria-label="Send"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 19V5M5 12l7-7 7 7"
                  stroke="#fff"
                  strokeWidth={2.5}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}

          {step === 'email' && (
            <button
              type="button"
              onClick={onEmailSubmit}
              disabled={!email.trim() || loading}
              className={cn(styles.ctaBtn, (!email.trim() || loading) && styles.ctaBtnInactive)}
            >
              {loading ? 'Sending…' : 'Send code'}
            </button>
          )}

          {step === 'code' && (
            <button
              type="button"
              onClick={onCodeSubmit}
              disabled={!code.trim() || loading}
              className={cn(styles.ctaBtn, (!code.trim() || loading) && styles.ctaBtnInactive)}
            >
              {loading ? 'Verifying…' : 'Verify'}
            </button>
          )}
        </div>
      </div>

      {successMessage && <p className={styles.success}>{successMessage}</p>}

      {!successMessage && errorMessage && errorMessage !== 'application_already_added' && (
        <p className={styles.error}>{errorMessage}</p>
      )}

      {step === 'prompt' && (
        <div className={styles.chips}>
          {normalizedChips.map((chip, index) => (
            <button
              key={chip.label}
              type="button"
              className={cn(styles.chip, shouldType && index === typingChip && styles.chipActive)}
              data-chip={chip.query}
              data-index={index}
              onClick={handleChipClick}
            >
              {chip.label}
            </button>
          ))}
        </div>
      )}

      {step === 'code' && (
        <p className={styles.codeHint}>
          Code sent to <strong>{email}</strong>. Check your inbox.
        </p>
      )}
    </div>
  );
};
